import React, { useState, useEffect } from 'react';
import JsBarcode from 'jsbarcode';
import { createCanvas } from 'canvas';

const MarriageCertificate = () => {
    const [formData, setFormData] = useState({
        token_id: 'E Marriage(C) -',
        local_no: '',
        date: '',
        name: '',
        father_name: '',
        cnic: '',
        dob: '',
        remarks: '',
        phone_no: '',
        status: ''
    });

    const [message, setMessage] = useState(null);
    const [barcodeImage, setBarcodeImage] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const generateBarcode = (tokenId) => {
        try {
            const canvas = createCanvas();
            JsBarcode(canvas, tokenId, { 
                format: "CODE128",
                width: 2,
                height: 100,
                displayValue: true
            });
            return canvas.toDataURL("image/png");
        } catch (error) {
            console.error("Barcode generation error:", error);
            return null;
        }
    };

    useEffect(() => {
        if (formData.token_id && formData.token_id.trim() !== '') {
            const barcode = generateBarcode(formData.token_id);
            setBarcodeImage(barcode);
        } else {
            setBarcodeImage(null);
        }
    }, [formData.token_id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        
        // Clear message when user starts typing
        if (message) {
            setMessage(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Reset message
        setMessage(null);

        if (!formData.token_id || !formData.local_no || !formData.date) {
            setMessage("Please fill in the required fields: Token ID, No, and Date.");
            return;
        }

        setIsSubmitting(true);

        try {
            console.log("Submitting Marriage Cert data:", formData);
            
            const response = await fetch('https://chaman.gob.pk/eac_backend/marriagecert.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'add',
                    ...formData,
                    barcode: barcodeImage || ''
                })
            });

            console.log("Response status:", response.status);
            
            // Check if response is OK
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const result = await response.json();
            console.log("Server response:", result);
            
            if (result.status === "success") {
                setMessage(result.message);
                setFormData({
                    token_id: 'E Marriage(C) -',
                    local_no: '',
                    date: '',
                    name: '',
                    father_name: '',
                    cnic: '',
                    dob: '',
                    remarks: '',
                    phone_no: '',
                    status: ''
                });
                setBarcodeImage(null);
                setShowPopup(true);
                setTimeout(() => setShowPopup(false), 2000);
            } else {
                // Handle specific database error
                if (result.message && result.message.includes("Field 'id' doesn't have a default value")) {
                    setMessage("Database Configuration Error: Please contact administrator to fix database settings.");
                } else {
                    setMessage("Error: " + (result.message || "Unknown error occurred"));
                }
                console.error("Server error:", result);
            }
        } catch (error) {
            console.error("Error submitting Marriage Certificate form:", error);
            
            // Handle fetch errors
            if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
                setMessage("Network error: Please check your internet connection and try again.");
            } else {
                setMessage("An error occurred while submitting the form. Please try again.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="lc-form-container">
            {/* Display error/success message */}
            {message && (
                <div className={`lc-message ${message.includes("success") ? 'success' : 'error'}`}>
                    {message}
                    <button 
                        onClick={() => setMessage(null)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                            marginLeft: '10px',
                            fontSize: '18px'
                        }}
                    >
                        ×
                    </button>
                </div>
            )}
            
            <form onSubmit={handleSubmit} className="lc-form">
                <div className="lc-left-section">
                    <div className="lc-form-group">
                        <label className="lc-label">Token ID:</label>
                        <div className="lc-input-container">
                            <input 
                                type="text" 
                                name="token_id" 
                                value={formData.token_id} 
                                onChange={handleChange} 
                                className="lc-input"
                                required 
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <div className="lc-form-group">
                        <label className="lc-label">No:</label>
                        <div className="lc-input-container">
                            <input 
                                type="text" 
                                name="local_no" 
                                value={formData.local_no} 
                                onChange={handleChange} 
                                className="lc-input"
                                required 
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <div className="lc-form-group">
                        <label className="lc-label">Date:</label>
                        <div className="lc-input-container">
                            <input 
                                type="date" 
                                name="date" 
                                value={formData.date} 
                                onChange={handleChange} 
                                className="lc-input"
                                required 
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <div className="lc-form-group">
                        <label className="lc-label">Husband & Father Name:</label>
                        <div className="lc-input-container">
                            <input 
                                type="text" 
                                name="name" 
                                value={formData.name} 
                                onChange={handleChange} 
                                className="lc-input"
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <div className="lc-form-group">
                        <label className="lc-label">Wife & Father Name:</label>
                        <div className="lc-input-container">
                            <input 
                                type="text" 
                                name="father_name" 
                                value={formData.father_name} 
                                onChange={handleChange} 
                                className="lc-input"
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>
                </div>

                <div className="lc-right-section">
                    <div className="lc-form-group">
                        <label className="lc-label">CNIC:</label>
                        <div className="lc-input-container">
                            <input 
                                type="text" 
                                name="cnic" 
                                value={formData.cnic} 
                                onChange={handleChange} 
                                className="lc-input"
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <div className="lc-form-group">
                        <label className="lc-label">Date of Marriage:</label>
                        <div className="lc-input-container">
                            <input 
                                type="date" 
                                name="dob" 
                                value={formData.dob} 
                                onChange={handleChange} 
                                className="lc-input"
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <div className="lc-form-group">
                        <label className="lc-label">Remarks:</label>
                        <div className="lc-input-container">
                            <input 
                                type="text" 
                                name="remarks" 
                                value={formData.remarks} 
                                onChange={handleChange} 
                                className="lc-input"
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <div className="lc-form-group">
                        <label className="lc-label">Phone No:</label>
                        <div className="lc-input-container">
                            <input 
                                type="text" 
                                name="phone_no" 
                                value={formData.phone_no} 
                                onChange={handleChange} 
                                className="lc-input"
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <div className="lc-form-group">
                        <label className="lc-label">Status:</label>
                        <div className="lc-input-container">
                            <select 
                                name="status" 
                                value={formData.status} 
                                onChange={handleChange}
                                className="lc-select"
                                disabled={isSubmitting}
                            >
                                <option value="" disabled>Select Status</option>
                                <option value="Received">Received</option>
                                <option value="Ready">Ready</option>
                            </select>
                        </div>
                    </div>
                </div>

                <button 
                    type="submit" 
                    className="lc-submit-btn"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span style={{
                                display: 'inline-block',
                                width: '16px',
                                height: '16px',
                                border: '2px solid #fff',
                                borderTop: '2px solid transparent',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                marginRight: '8px'
                            }}></span>
                            Submitting...
                        </>
                    ) : 'Submit'}
                </button>
            </form>

            {showPopup && (
                <div className="lc-popup">
                    Data saved successfully!
                </div>
            )}
            
            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .lc-message {
                    padding: 10px 15px;
                    margin-bottom: 15px;
                    border-radius: 4px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .lc-message.success {
                    background-color: #d4edda;
                    color: #155724;
                    border: 1px solid #c3e6cb;
                }
                .lc-message.error {
                    background-color: #f8d7da;
                    color: #721c24;
                    border: 1px solid #f5c6cb;
                }
                .lc-submit-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
            `}</style>
        </div>
    );
};

export default MarriageCertificate;